import * as yup from 'yup';
import { isValidYupSchema, yupUtils } from '../../utils/yup/utils';
import { ProjectErrors, ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorForCode, getProjectErrorFromRawError, isProjectError } from '../../utils/errors';
import { Category, AuthorCategory, Goal, Pillar, Promotion } from '../types/contentManagement';
import { ContentGroupSchema } from '../contentGroup/parser';
import { LessonSchema } from '../lesson/parser';

export const MinimalCategorySchema = yup.object().shape({
  name: yupUtils.stringRequired('The category name is required'),
});

export const CategorySchema = yup.object().shape({
  id: yupUtils.stringRequired(),
  name: yupUtils.stringRequired('The category name is required'),
});

export const MinimalAuthorCategorySchema = yup.object().shape({
  name: yupUtils.stringRequired('The author category name is required'),
});

export const AuthorCategorySchema = yup.object().shape({
  id: yupUtils.stringRequired(),
  name: yupUtils.stringRequired('The author category name is required'),
});

export const MinimalGoalSchema = yup.object().shape({
  key: yupUtils.stringRequired('The goal key is required'),
  name: yupUtils.stringRequired('The goal name is required'),
});

export const GoalSchema = yup.object().shape({
  id: yupUtils.stringRequired(),
  name: yupUtils.stringRequired('The goal name is required'),
  linkedCategories: yupUtils.arrayOptionalNullableOf(yupUtils.stringRequired()),
  linkedPillars: yupUtils.arrayOptionalNullableOf(yupUtils.stringRequired()),
  imagePath: yupUtils.stringOptionalNullable,
  featuredCollection: yupUtils.arrayOptionalNullableOf(ContentGroupSchema),
  favoriteLessons: yupUtils.arrayOptionalNullableOf(LessonSchema),
  emoji: yupUtils.stringOptionalNullable,
  color: yupUtils.stringOptionalNullable,
  order: yupUtils.numberOptionalNullable,
});

export const PillarSchema = yup.object().shape({
  id: yupUtils.stringRequired(),
  name: yupUtils.stringRequired(),
  imagePath: yupUtils.stringOptionalNullable,
});

export const PromotionSchema = yup.object().shape({
  id: yupUtils.stringRequired(),
  title: yupUtils.stringRequired(),
  offerId: yupUtils.stringRequired(),
  timer: yupUtils.numberOptionalNullable,
  createdAt: yupUtils.numberRequired,
  updatedAt: yupUtils.numberRequired,
  startDate: yupUtils.numberRequired,
  endDate: yupUtils.numberRequired,
  description: yupUtils.stringOptionalNullable,
  buttonText: yupUtils.stringOptionalNullable,
  imagePath: yupUtils.stringOptionalNullable,
});

export const parseCategory = async (object: any): Promise<Category | ProjectErrorType> => {
  try {
    if (await isValidYupSchema(CategorySchema, object)) {
      return object as Category;
    }
    return getProjectErrorForCode(ProjectErrors.invalidCategory);
  } catch (error) {
    console.log(`[CATEGORIES] ID: ${object?.id}. Error:`, error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const parseCategories = async (items: any): Promise<Category[]> => {
  if (!(items && Array.isArray(items))) {
    console.log('[CATEGORIES] Error: No array found.');
    return [];
  }

  const data: Category[] = [];
  for (const item of items) {
    const o = await parseCategory(item);
    if (o && !isProjectError(o)) {
      data.push(o as Category);
    }
  }

  return data;
};

export const parseAuthorCategory = async (object: any): Promise<AuthorCategory | ProjectErrorType> => {
  try {
    if (await isValidYupSchema(AuthorCategorySchema, object)) {
      return object as AuthorCategory;
    }
    return getProjectErrorForCode(ProjectErrors.invalidAuthorCategory);
  } catch (error) {
    console.log(`[AUTHOR_CATEGORIES] ID: ${object?.id}. Error:`, error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const parseAuthorCategories = async (items: any): Promise<AuthorCategory[]> => {
  if (!(items && Array.isArray(items))) {
    console.log('[AUTHOR_CATEGORIES] Error: No array found.');
    return [];
  }

  const data: AuthorCategory[] = [];
  for (const item of items) {
    const o = await parseAuthorCategory(item);
    if (o && !isProjectError(o)) {
      data.push(o as AuthorCategory);
    }
  }

  return data;
};

export const parseGoal = async (object: any): Promise<Goal | ProjectErrorType> => {
  try {
    if (await isValidYupSchema(GoalSchema, object)) {
      return object as Goal;
    }
    return getProjectErrorForCode(ProjectErrors.invalidGoal);
  } catch (error) {
    console.log(`[GOALS] ID: ${object?.id}. Error:`, error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const parseGoals = async (items: any): Promise<Goal[]> => {
  if (!(items && Array.isArray(items))) {
    console.log('[GOALS] Error: No array found.');
    return [];
  }

  const data: Goal[] = [];
  for (const item of items) {
    const o = await parseGoal(item);
    if (o && !isProjectError(o)) {
      data.push(o as Goal);
    }
  }

  return data;
};

export const parsePillar = async (object: any): Promise<Pillar | ProjectErrorType> => {
  try {
    if (await isValidYupSchema(PillarSchema, object)) {
      return object as Pillar;
    }
    return getProjectErrorForCode(ProjectErrors.invalidPillar);
  } catch (error) {
    console.log(`[PILLARS] ID: ${object?.id}. Error:`, error);
    return getProjectErrorFromRawError(error as Error);
  }
};
export const parsePillars = async (items: any): Promise<Pillar[]> => {
  if (!(items && Array.isArray(items))) {
    console.log('[PILLARS] Error: No array found.');
    return [];
  }

  const data: Pillar[] = [];
  for (const item of items) {
    const o = await parsePillar(item);
    if (o && !isProjectError(o)) {
      data.push(o as Pillar);
    }
  }

  return data;
};

export const parsePromotion = async (object: any): Promise<Promotion | ProjectErrorType> => {
  try {
    if (await isValidYupSchema(PromotionSchema, object)) {
      return object as Promotion;
    }
    return getProjectErrorForCode(ProjectErrors.invalidPromotion);
  } catch (error) {
    console.log(`[PROMOTIONS] ID: ${object?.id}. Error:`, error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const parsePromotions = async (items: any): Promise<Promotion[]> => {
  if (!(items && Array.isArray(items))) {
    console.log('[PROMOTIONS] Error: No array found.');
    return [];
  }

  const data: Promotion[] = [];
  for (const item of items) {
    const o = await parsePromotion(item);
    if (o && !isProjectError(o)) {
      data.push(o as Promotion);
    }
  }

  return data;
};

export const MinimalPromotionSchema = yup.object().shape({
  title: yupUtils.stringRequired('The promotion title is required'),
  offerId: yupUtils.stringRequired('The offer ID is required'),
  startDate: yupUtils.numberRequired,
  endDate: yupUtils.numberRequired,
});
